// Annotaties als technische tekening. Twee taken per figuur:
// 1. Nummers op de foto die elkaar zouden raken schuiven iets naar beneden,
//    met een lijntje naar het echte punt.
// 2. Vanaf 64rem staan de teksten als callouts in de kolom naast de foto,
//    gesorteerd op hoogte en zonder overlap, elk met een verwijslijn.
// Alles wordt opnieuw berekend bij resize en zodra de foto geladen is.

const DESKTOP = '(min-width: 64rem)';
const NS = 'http://www.w3.org/2000/svg';

// Elke lijn krijgt een lichte rand eronder, zodat hij ook op een donker doek leesbaar blijft.
function lijn(svg, punten, klasse, i) {
    const pts = punten.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    for (const extra of ['annotaties__lijn-rand', klasse]) {
        const el = document.createElementNS(NS, 'polyline');
        el.setAttribute('points', pts);
        el.setAttribute('class', extra);
        el.dataset.i = i;
        svg.appendChild(el);
    }
}

function leg(figuur) {
    const vlak = figuur.querySelector('.annotaties__vlak');
    const foto = figuur.querySelector('.annotaties__foto');
    const kolom = figuur.querySelector('.annotaties__kolom');
    const svg = figuur.querySelector('.annotaties__lijnen');
    const knoppen = [...figuur.querySelectorAll('.annotatie')];
    if (!vlak || !foto || !svg || !knoppen.length) return;

    const vlakRect = vlak.getBoundingClientRect();
    const fotoRect = foto.getBoundingClientRect();
    const fotoX = fotoRect.left - vlakRect.left;
    const fotoY = fotoRect.top - vlakRect.top;
    const W = fotoRect.width;
    const H = fotoRect.height;
    if (!W || !H) return;

    svg.setAttribute('viewBox', `0 0 ${vlakRect.width} ${vlakRect.height}`);
    svg.setAttribute('width', vlakRect.width);
    svg.setAttribute('height', vlakRect.height);
    svg.replaceChildren();

    // 1. Nummers: op hun punt, tenzij ze een eerder nummer raken.
    const maat = knoppen[0].getBoundingClientRect().width || 36;
    const ruimte = 6;
    const items = knoppen.map((knop) => ({
        knop,
        i: Number(knop.dataset.i),
        px: (parseFloat(knop.dataset.x) / 100) * W,
        py: (parseFloat(knop.dataset.y) / 100) * H,
    }));
    items.forEach((it) => { it.cx = it.px; it.cy = it.py; });
    const opVolgorde = [...items].sort((a, b) => a.py - b.py || a.px - b.px);
    const geplaatst = [];
    for (const it of opVolgorde) {
        let bewogen = true;
        let rondes = 0;
        while (bewogen && rondes < 12) {
            bewogen = false;
            rondes++;
            for (const ander of geplaatst) {
                if (Math.abs(it.cx - ander.cx) < maat + ruimte && Math.abs(it.cy - ander.cy) < maat + ruimte) {
                    it.cy = ander.cy + maat + ruimte;
                    bewogen = true;
                }
            }
        }
        it.cy = Math.min(Math.max(it.cy, maat / 2 + 2), H - maat / 2 - 2);
        it.cx = Math.min(Math.max(it.cx, maat / 2 + 2), W - maat / 2 - 2);
        geplaatst.push(it);
    }
    for (const it of items) {
        const verschoven = Math.abs(it.cy - it.py) > 2 || Math.abs(it.cx - it.px) > 2;
        it.knop.style.top = `${(it.cy / H) * 100}%`;
        it.knop.style.left = `${(it.cx / W) * 100}%`;
        it.knop.classList.toggle('is-verschoven', verschoven);
        if (verschoven) {
            lijn(svg, [[fotoX + it.cx, fotoY + it.cy], [fotoX + it.px, fotoY + it.py]], 'annotaties__lijn annotaties__lijn--punt', it.i);
        }
    }

    // 2. Callouts in de kolom (alleen desktop).
    if (!kolom || !window.matchMedia(DESKTOP).matches) return;
    const kolomRect = kolom.getBoundingClientRect();
    const kolomX = kolomRect.left - vlakRect.left;
    const labels = [...kolom.querySelectorAll('.annotaties__label')];
    const gap = 10;
    const perIndex = new Map(items.map((it) => [it.i, it]));
    const rijen = labels.map((label) => {
        const it = perIndex.get(Number(label.dataset.i));
        return { label, it, h: label.offsetHeight, y: it ? it.cy - label.offsetHeight / 2 : 0 };
    }).sort((a, b) => (a.it?.cy ?? 0) - (b.it?.cy ?? 0));

    // Van boven naar beneden uit elkaar duwen, daarna terug binnen de kolom.
    let onder = 0;
    for (const r of rijen) { r.y = Math.max(r.y, onder); onder = r.y + r.h + gap; }
    let boven = Math.max(H, onder - gap);
    for (let k = rijen.length - 1; k >= 0; k--) {
        const r = rijen[k];
        r.y = Math.min(r.y, boven - r.h);
        boven = r.y - gap;
    }
    const minY = Math.min(...rijen.map((r) => r.y));
    const schuif = minY < 0 ? -minY : 0;

    for (const r of rijen) {
        const y = r.y + schuif;
        r.label.style.top = `${y}px`;
        if (!r.it) continue;
        const start = [fotoX + r.it.cx + maat / 2, fotoY + r.it.cy];
        const knik = [kolomX - 14, fotoY + y + r.h / 2];
        const eind = [kolomX, fotoY + y + r.h / 2];
        lijn(svg, [start, knik, eind], 'annotaties__lijn', r.it.i);
    }
    kolom.style.minHeight = `${Math.max(H, onder - gap + schuif)}px`;
}

export default function maakAnnotaties() {
    const figuren = document.querySelectorAll('[data-annotaties]');
    if (!figuren.length) return;

    const alles = () => figuren.forEach(leg);
    let timer = null;
    window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(alles, 120); }, { passive: true });
    window.matchMedia(DESKTOP).addEventListener('change', alles);

    figuren.forEach((figuur) => {
        const img = figuur.querySelector('img');
        if (img && !img.complete) img.addEventListener('load', () => leg(figuur), { once: true });
        if ('fonts' in document) document.fonts.ready.then(() => leg(figuur));
        leg(figuur);
    });
}
